import { useState, useCallback, useEffect } from "react";
import { ListTree } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface TocEntry {
  level: number;
  text: string;
  element: HTMLElement;
}

/**
 * Scans the Milkdown (ProseMirror) editor DOM for heading elements
 * and returns structured ToC entries.
 */
function scanEditorHeadings(): TocEntry[] {
  const editorEl = document.querySelector(".ProseMirror");
  if (!editorEl) return [];

  const headings = editorEl.querySelectorAll("h1, h2, h3, h4, h5, h6");
  const entries: TocEntry[] = [];
  headings.forEach((h) => {
    const levelToken = h.tagName.charAt(1);
    const level = Number.parseInt(levelToken, 10);
    if (Number.isNaN(level)) return;
    const text = h.textContent?.trim() || "";
    if (text) {
      entries.push({ level, text, element: h as HTMLElement });
    }
  });
  return entries;
}

/**
 * A button that opens a popover showing the table of contents
 * derived from headings in the currently rendered markdown document.
 * Clicking an entry scrolls the editor to that heading.
 */
export const TableOfContentsButton = () => {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<TocEntry[]>([]);

  const refresh = useCallback(() => {
    setEntries(scanEditorHeadings());
  }, []);

  // Re-scan headings each time the popover opens
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleClick = (entry: TocEntry) => {
    entry.element.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  const minLevel =
    entries.length > 0 ? Math.min(...entries.map((e) => e.level)) : 1;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-muted-foreground hover:text-foreground"
          title="Table of contents"
        >
          <ListTree className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={4}
        className="w-64 max-h-[min(70vh,700px)] overflow-auto p-1"
      >
        {entries.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-muted-foreground">
            No headings found
          </p>
        ) : (
          <nav className="flex flex-col">
            {entries.map((entry, i) => (
              <button
                key={i}
                type="button"
                className="w-full text-left rounded-sm px-2 py-0.5 text-sm hover:bg-accent hover:text-accent-foreground truncate cursor-pointer"
                style={{
                  paddingLeft: `${(entry.level - minLevel) * 16 + 8}px`,
                }}
                onClick={() => handleClick(entry)}
                title={entry.text}
              >
                {entry.text}
              </button>
            ))}
          </nav>
        )}
      </PopoverContent>
    </Popover>
  );
};
